<template>
  <div
    ref="wrapperRef"
    class="charts-wrapper"
    :class="{ 'is-mobile-landscape': isMobileLandscape }"
  >
    <div class="symbals-wrapper">
      <div class="symbals">
        <div class="symbal-block">
          <img src="@/assets/svg/chart-icons/banker.svg" alt="Banker count" />
          {{ chartFightsTotal.bankerCount }}
        </div>
        <div class="symbal-block">
          <img src="@/assets/svg/chart-icons/player.svg" alt="Player count" />
          {{ chartFightsTotal.playerCount }}
        </div>
        <div class="symbal-block">
          <img src="@/assets/svg/chart-icons/draw.svg" alt="Draw count" />
          {{ chartFightsTotal.drawCount }}
        </div>
      </div>
      <div class="chart-forcast">
        <div class="forcast player">
          <div class="label">P?</div>
          <ForcastImages
            :bigEye="playerForcast.bigEye"
            :smallRoad="playerForcast.smallRoad"
            :cockroachRoad="playerForcast.cockroachRoad"
          />
        </div>
        <div class="forcast banker">
          <div class="label">B?</div>
          <ForcastImages
            :bigEye="bankerForcast.bigEye"
            :smallRoad="bankerForcast.smallRoad"
            :cockroachRoad="bankerForcast.cockroachRoad"
          />
        </div>
      </div>
    </div>
    <div
      ref="chartsRef"
      class="charts-content"
      :style="{
        cursor: isDragging ? 'grabbing' : 'grab',
        scrollSnapType: isDragging ? '' : '',
      }"
      @mousedown="onMouseDown"
      @mouseup="onMouseUp"
    >
      <div class="right-charts">
        <div class="road-chart">
          <Chart
            :key="`road-${roadChartProps.col}`"
            :chartType="ChartTypes.road"
            :chartDataType="ChartDataType.image"
            :pointList="getRoadChartData"
            :fightList="getRoadChartFightData"
            :imageList="RoadImageList"
            :chartProps="roadChartProps"
            :isShowTooltip="true"
            :chartFights="chartFights"
          />
        </div>
        <div class="small-charts" :class="{ 'is-mobile': isMobile }">
          <div class="big-eye-road-chart">
            <Chart
              :key="`small-${smallRoadChartProps.col}`"
              :chartType="ChartTypes.road"
              :chartDataType="ChartDataType.image"
              :pointList="bigEyeRoadChartData"
              :imageList="BEImageList"
              :chartProps="smallRoadChartProps"
            />
          </div>
          <div class="small-road-chart">
            <Chart
              :key="`small-${smallRoadChartProps.col}`"
              :chartType="ChartTypes.road"
              :chartDataType="ChartDataType.image"
              :pointList="smallRoadChartData"
              :imageList="SRImageList"
              :chartProps="smallRoadChartProps"
            />
          </div>
          <div class="cockroach-pic-chart">
            <Chart
              :key="`small-${smallRoadChartProps.col}`"
              :chartType="ChartTypes.road"
              :chartDataType="ChartDataType.image"
              :pointList="cockroachRoadChartData"
              :imageList="CRImageList"
              :chartProps="smallRoadChartProps"
            />
          </div>
        </div>
      </div>
      <div class="left-charts">
        <Chart
          :chartType="ChartTypes.summary"
          :chartDataType="ChartDataType.image"
          :pointList="getSummeryChartData"
          :imageList="SummeryImageList"
          :chartProps="summeryRoadChartProps"
          :chartFights="chartFights"
          :fightListSummary="fightListSummary"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { ChartTypes, ChartDataType, SmallChartTypes } from '@/utils/index.model';
import bankerIcon from '@/assets/svg/chart-icons/banker.svg';
import playerIcon from '@/assets/svg/chart-icons/player.svg';
import drawIcon from '@/assets/svg/chart-icons/draw.svg';
import playerDrawIcon from '@/assets/svg/chart-icons/player-draw.svg';
import bankerDrawIcon from '@/assets/svg/chart-icons/banker-draw.svg';

import BEBanker from '@/assets/svg/chart-icons/banker-outline.svg';
import BEPlayer from '@/assets/svg/chart-icons/player-outline.svg';

import SRBanker from '@/assets/svg/chart-icons/banker-filled.svg';
import SRPlayer from '@/assets/svg/chart-icons/player-filled.svg';

import CRBanker from '@/assets/svg/chart-icons/banker-line.svg';
import CRPlayer from '@/assets/svg/chart-icons/player-line.svg';
import {
  convertDataToSmallMapSource,
  smallChartDataArr,
  getSmallChartForcastValue,
} from '@/utils/charts/mini-game-chart';
import ForcastImages from '@/components/charts/forcast-images.vue';
import { fightsListToChartData, fightsListToFightData } from '@/utils/helpers';
import { mapGetters } from 'vuex';
import { takeRight } from 'lodash';
import { isMobile } from 'mobile-device-detect';

export default {
  components: {
    ForcastImages,
    Chart: () => import('@/components/charts/basic-chart.vue'),
  },
  data() {
    return {
      ChartTypes,
      ChartDataType,
      roadChartPointList: [],
      RoadImageList: [bankerIcon, playerIcon, bankerDrawIcon, playerDrawIcon],
      SummeryImageList: [bankerIcon, playerIcon, drawIcon],
      BEImageList: [BEBanker, BEPlayer],
      SRImageList: [SRBanker, SRPlayer],
      CRImageList: [CRBanker, CRPlayer],
      playerForcast: {
        bigEye: -1,
        smallRoad: -1,
        cockroachRoad: -1,
      },
      bankerForcast: {
        bigEye: -1,
        smallRoad: -1,
        cockroachRoad: -1,
      },
      summeryCounter: 1,
      isDragging: false,
      cursorPos: [0, 0],
      isMobile,
      fightListSummary: [],
      // Own content width in layout px (local to any zoom on an ancestor), when fluid.
      availableWidth: 0,
    };
  },
  props: {
    /*
     * Desktop pane: lay the roads out to the width they are given, as the GA28
     * prototype's CfRoad does (its big road is flex: 1), rather than at the
     * fixed 21 columns -- which left the right of a wide pane empty and
     * scrolled in a narrow one. The cells keep their size; the road gets as
     * many columns as fit, the three small roads share its width.
     */
    fluid: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    window.addEventListener('mouseup', this.onMouseUp);
    if (this.fluid) this.watchWidth();
  },
  destroyed() {
    window.removeEventListener('mouseup', this.onMouseUp);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  computed: {
    ...mapGetters('fight', ['chartFights', 'chartFightsTotal']),
    isMobileLandscape() {
      return isMobile && this.$screen.width > this.$screen.height;
    },
    getChartData() {
      return this.getRoadChartDataReal.filter((v) => v < 3);
    },
    getChartFightData() {
      return this.getRoadChartFightDataReal.filter((v) => v.result < 3);
    },
    getSummeryChartData() {
      this.fightListSummary = [];
      this.chartFights.slice(-(30 + this.summeryCounter)).forEach((fight) => {
        this.fightListSummary.push(fight);
      });
      if (this.getChartData.length <= 36) return this.getChartData;
      return takeRight(this.getChartData, 30 + this.summeryCounter);
    },
    getRoadChartData() {
      const roadDataArr = [];
      let j = 0;
      this.getChartData.forEach((v, i) => {
        if (v === 2 && j === 0) {
          roadDataArr[0] = (roadDataArr[0] || 0) + v;
        }
        if (v === 2 && j !== 0 && roadDataArr[j - 1] < 2) {
          roadDataArr[j - 1] = roadDataArr[j - 1] + v;
        }
        if (v !== 2) {
          roadDataArr[j] = v;
          j++;
        }
      });
      return roadDataArr;
    },
    getRoadChartFightData() {
      const roadDataArr = [];
      let j = 0;
      this.getChartFightData.forEach((v) => {
        if (v.result === 2 && j === 0) {
          roadDataArr[0] = {
            fight: v.fight,
            result: (roadDataArr[0] && roadDataArr[0].result ? roadDataArr[0].result : 0) + v.result,
            previousResult: null,
          };
        }
        if (v.result === 2 && j !== 0 && roadDataArr[j - 1] && roadDataArr[j - 1].result < 2) {
          const previousResult = roadDataArr[j - 1].result;
          const previousFightNumber = roadDataArr[j - 1].fight;
          const previousFight = this.chartFights.find((f) => f.fightNumber === previousFightNumber);

          roadDataArr[j - 1] = {
            fight: v.fight,
            result: roadDataArr[j - 1].result + v.result,
            previousResult: previousResult,
            previousFight: roadDataArr[j - 1].fight,
            previousFightObj: previousFight,
          };
        }
        if (v.result !== 2) {
          roadDataArr[j] = {
            fight: v.fight,
            result: v.result,
            previousResult: undefined,
          };
          j++;
        }
      });
      return roadDataArr;
    },
    getRoadChartDataReal() {
      return fightsListToChartData(this.chartFights);
    },
    getRoadChartFightDataReal() {
      return fightsListToFightData(this.chartFights);
    },
    getSmallMapSourceArr() {
      return convertDataToSmallMapSource(this.getChartData);
    },
    bigEyeRoadChartData() {
      this.getForcastValue();
      return smallChartDataArr(this.getSmallMapSourceArr, SmallChartTypes.bigEye);
    },
    smallRoadChartData() {
      return smallChartDataArr(this.getSmallMapSourceArr, SmallChartTypes.smallRoad);
    },
    cockroachRoadChartData() {
      return smallChartDataArr(this.getSmallMapSourceArr, SmallChartTypes.cockroachRoad);
    },
    generalChartProps() {
      return {
        col: isMobile ? 13 : 21,
        row: 6,
        gap: 12,
        imgSize: 10,
        imgOffset: 1,
        textColor: null,
        lineScale: 1,
        gridLineColor: '#2e4c57',
        gridBorderColor: '',
      };
    },
    // Columns of the big road that fit beside the summary, when fluid.
    fluidRoadCols() {
      if (!this.fluid || !this.availableWidth) return 0;
      const gap = 24;
      const summary = 6 * 38 + 8 + 4; // summary beads + the gap before them + both canvases' 1px borders
      return Math.max(10, Math.floor((this.availableWidth - summary) / gap));
    },
    // The three small roads side by side (15px apart) under the big road.
    fluidSmallCols() {
      if (!this.fluidRoadCols) return 0;
      return Math.max(5, Math.floor((this.fluidRoadCols * 24 - 2 * 15) / 3 / 12));
    },
    roadChartProps() {
      return {
        ...this.generalChartProps,
        col: this.isMobileLandscape ? 15 : this.fluidRoadCols || this.generalChartProps.col,
        gap: this.isMobileLandscape ? 16 : 24,
        imgSize: this.isMobileLandscape ? 12 : 18,
        imgOffset: 1,
      };
    },
    smallRoadChartProps() {
      return {
        ...this.generalChartProps,
        gap: this.isMobileLandscape ? 9 : 12,
        imgSize: this.isMobileLandscape ? 6 : 10,
        col: isMobile ? 8 : this.fluidSmallCols || 13,
      };
    },
    summeryRoadChartProps() {
      return {
        ...this.generalChartProps,
        col: 6,
        gap: this.isMobileLandscape ? 26 : 38,
        imgSize: this.isMobileLandscape ? 18 : 28,
        imgOffset: 1,
        gridLineColor: '',
        gridBorderColor: '',
      };
    },
  },
  methods: {
    watchWidth() {
      const measure = () => {
        const el = this.$refs.wrapperRef;
        if (!el) return;
        // clientWidth counts the wrapper's padding, which the roads cannot use:
        // sized to it, they ran 16px wider than the box and scrolled sideways.
        const style = getComputedStyle(el);
        this.availableWidth = el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      };
      measure();
      if (typeof ResizeObserver === 'undefined') return;
      this.resizeObserver = new ResizeObserver(measure);
      this.resizeObserver.observe(this.$refs.wrapperRef);
    },
    getFightForRoadPosition(roadIndex) {
      // Get the original data and the transformed road data
      const originalData = this.getChartData;
      const roadData = this.getRoadChartData;

      // Initialize mapping variables
      let originalIndex = 0;
      let roadArrayIndex = 0;

      // Map from road index to original data index
      while (originalIndex < originalData.length && roadArrayIndex <= roadIndex) {
        const value = originalData[originalIndex];

        if (value === 2) {
          // Draw
          // Draw results are combined with previous results in roadData
          if (roadArrayIndex > 0) {
            // Skip this draw in original data
            originalIndex++;
            continue;
          } else if (roadArrayIndex === 0) {
            // Special case for first position
            originalIndex++;
            continue;
          }
        }

        // For regular results (banker or player)
        if (roadArrayIndex === roadIndex) {
          // We found our position
          break;
        }

        roadArrayIndex++;
        originalIndex++;
      }

      // Get the corresponding fight from chartFights
      // Note: chartFights is already reversed in fightsListToChartData
      return this.chartFights[originalIndex];
    },
    getForcastValue() {
      if (this.getSmallMapSourceArr.length === 0) return;
      const forcastVal = getSmallChartForcastValue(this.getSmallMapSourceArr, SmallChartTypes.cockroachRoad);
      this.playerForcast = {
        bigEye: forcastVal.bigEye < 0 ? -1 : forcastVal.isBanker ? (forcastVal.bigEye + 1) % 2 : forcastVal.bigEye,
        smallRoad:
          forcastVal.smallRoad < 0 ? -1 : forcastVal.isBanker ? (forcastVal.smallRoad + 1) % 2 : forcastVal.smallRoad,
        cockroachRoad:
          forcastVal.cockroachRoad < 0
            ? -1
            : forcastVal.isBanker
            ? (forcastVal.cockroachRoad + 1) % 2
            : forcastVal.cockroachRoad,
      };
      this.bankerForcast = {
        bigEye: forcastVal.bigEye < 0 ? -1 : !forcastVal.isBanker ? (forcastVal.bigEye + 1) % 2 : forcastVal.bigEye,
        smallRoad:
          forcastVal.smallRoad < 0 ? -1 : !forcastVal.isBanker ? (forcastVal.smallRoad + 1) % 2 : forcastVal.smallRoad,
        cockroachRoad:
          forcastVal.cockroachRoad < 0
            ? -1
            : !forcastVal.isBanker
            ? (forcastVal.cockroachRoad + 1) % 2
            : forcastVal.cockroachRoad,
      };
    },
    onMouseDown(ev) {
      this.cursorPos = [ev.pageX, ev.pageY];
      this.isDragging = true;

      window.addEventListener('mousemove', this.onMouseHold);
    },
    onMouseUp(ev) {
      window.removeEventListener('mousemove', this.onMouseHold);
      this.isDragging = false;
    },
    onMouseHold(ev) {
      ev.preventDefault();

      requestAnimationFrame(() => {
        const delta = [ev.pageX - this.cursorPos[0], ev.pageY - this.cursorPos[1]];

        this.cursorPos = [ev.pageX, ev.pageY];

        if (!this.$refs.chartsRef) return;
        this.$refs.chartsRef.scrollBy({
          left: -delta[0],
          top: -delta[1],
        });
      });
    },
  },
  watch: {
    'chartFights.0.id': {
      handler(newVal) {
        if (!newVal) return;
        this.summeryCounter = this.summeryCounter % 6;
      },
    },
  },
};
</script>
<style lang="scss" scoped>
.charts-wrapper {
  width: 100%;
  font-size: 16px;
  color: #fff;
  padding: 8px;
  box-sizing: border-box;

  &.is-mobile-landscape {
    height: calc(100% - 240px);
    overflow-y: auto;
    padding: 0;
    margin-top: 10px;
  }

  @media (max-width: $tablet-portrait-width) {
    font-size: 12px;
    line-height: 16px;
  }

  img {
    width: 20px;
    height: 20px;

    @media (max-width: $tablet-portrait-width) {
      width: 13px;
      height: 13px;
    }
  }

  .symbals-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    font-size: 12px;

    .symbals {
      display: flex;
      justify-content: flex-start;

      .symbal-block {
        display: flex;
        align-items: center;
        margin-right: 8px;
        img {
          margin-right: 5px;
        }
      }
    }

    .chart-forcast {
      display: flex;
      justify-content: flex-end;

      .forcast {
        box-sizing: border-box;
        border-radius: 16px;
        text-transform: uppercase;
        display: flex;
        flex-direction: row;
        background-color: #000;
        padding: 0;
        margin-right: 8px;

        .label {
          display: flex;
          align-items: center;

          img {
            width: 16px;
            height: 16px;
            margin-left: 5px;

            @media (max-width: $tablet-portrait-width) {
              width: 12px;
              height: 12px;
              margin-left: 3px;
            }
          }
        }

        .label {
          width: 32px;
          height: 100%;
          display: inline-block;
          text-align: center;
          padding: 4px;
          border-radius: 10px;
          box-sizing: border-box;

          @media (max-width: $tablet-portrait-width) {
            padding: 0;
          }
        }

        &.banker .label {
          background-color: #ff0000;
        }

        &.player .label {
          background-color: #0e86d4;
        }
      }
    }
  }

  .charts-content {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    overflow-x: auto;

    .right-charts {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      margin-right: 8px;
    }

    .road-chart {
      background-color: #0c1721;
    }
    .small-charts {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      margin-top: 12px;

      & > div {
        background-color: #0c1721;
        margin-right: 15px;

        &:last-of-type {
          margin-right: 0;
        }
      }

      &.is-mobile {
        margin-top: 4px;
        & > div {
          margin-right: 10px;
          &:last-of-type {
            margin-right: 0;
          }
        }
      }
    }

    // firefox
    scrollbar-color: #1d2b38 #2e4c57;
    scrollbar-width: thin;
    width: 100%;

    &::-webkit-scrollbar {
      // -webkit-appearance: none;
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #2e4c57;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: #1d2b38;
    }
  }
}
</style>
